/**
 * App Configuration Utility
 * 
 * Provides safe access to window.APP_CONFIG with validation and error handling.
 * This config is set dynamically in public/config.js based on the hostname.
 */

/**
 * Get the API URL from APP_CONFIG (runtime config from config.js)
 * This is the single source of truth for API baseURL
 * @returns {string} The API base URL
 * @throws {Error} If config is not available
 */
export const getApiUrl = () => {
  if (!window.APP_CONFIG || !window.APP_CONFIG.REACT_APP_API_URL) {
    const error = new Error(
      'APP_CONFIG.REACT_APP_API_URL is not available. Make sure config.js is loaded before the React app.'
    );
    console.error('❌ Config Error:', error.message);
    throw error;
  }
  return window.APP_CONFIG.REACT_APP_API_URL;
};

/**
 * Get baseURL - alias for getApiUrl for convenience
 * Use this when you need the baseURL string (e.g., in template strings)
 * @returns {string} The API base URL
 */
export const getBaseURL = getApiUrl;

/**
 * Get the Frontend URL from APP_CONFIG
 * @returns {string} The Frontend URL
 */
export const getFeUrl = () => {
  return window.APP_CONFIG?.REACT_APP_FE_URL || window.location.origin;
};

/**
 * Get the S3 Bucket from APP_CONFIG
 * @returns {string} The S3 bucket name
 */
export const getS3Bucket = () => {
  return window.APP_CONFIG?.REACT_APP_S3_BUCKET || 'cp360.beyondqa';
};

/**
 * Get the S3 Bucket Call from APP_CONFIG
 * @returns {string} The S3 bucket call name
 */
export const getS3BucketCall = () => {
  return window.APP_CONFIG?.REACT_APP_S3_BUCKET_CALL || 'cp360.beyondqa';
};

/**
 * Get the AWS Region from APP_CONFIG
 * @returns {string} The AWS region
 */
export const getRegion = () => {
  return window.APP_CONFIG?.REACT_APP_REGION || 'us-east-2';
};

/**
 * Get the entire APP_CONFIG object
 * @returns {Object} The complete config object
 * @throws {Error} If config is not available
 */
export const getAppConfig = () => {
  if (!window.APP_CONFIG) {
    const error = new Error(
      'APP_CONFIG is not available. Make sure config.js is loaded before the React app.'
    );
    console.error('❌ Config Error:', error.message);
    throw error;
  }
  return window.APP_CONFIG;
};

/**
 * Check if APP_CONFIG is available
 * @returns {boolean} True if config is available
 */
export const isConfigAvailable = () => {
  return !!(window.APP_CONFIG && window.APP_CONFIG.REACT_APP_API_URL);
};

/**
 * Single source of truth for API baseURL
 * Use this function instead of process.env.REACT_APP_API_URL or window.APP_CONFIG.REACT_APP_API_URL
 * This automatically uses runtime config (window.APP_CONFIG) if available, otherwise falls back to process.env
 * 
 * Usage:
 *   import { API_BASE_URL } from '../utils/appConfig';
 *   const url = API_BASE_URL(); // Call as function
 */
export const API_BASE_URL = getApiUrl;

// Export default for convenience
export default {
  getApiUrl,
  getBaseURL,
  API_BASE_URL,
  getFeUrl,
  getS3Bucket,
  getS3BucketCall,
  getRegion,
  getAppConfig,
  isConfigAvailable
};

